<script setup lang="ts">
import { useSiteCurtailmentConfigurations } from '@/composables/eos/site-curtailment-configurations'
import type { ISiteCurtailmentConfigurationUpsertForm } from '@/types/eos/forms'
import type { ISiteCurtailmentConfiguration } from '@/types/eos/SiteCurtailmentConfiguration'
import { useTemplateRef } from 'vue'
import SiteCurtailmentConfigurationForm from './SiteCurtailmentConfigurationForm.vue'

interface SiteCurtailmentConfigurationDialogProps {
  siteCurtailmentConfiguration?: ISiteCurtailmentConfiguration
}

const props = defineProps<SiteCurtailmentConfigurationDialogProps>()

const dialogVisible = defineModel<boolean>()
const formRef = useTemplateRef('formRef')

const { updateSiteCurtailmentConfigurationMutation, createSiteCurtailmentConfigurationMutation } =
  useSiteCurtailmentConfigurations()
const {
  mutate: updateMutation,
  isPending: isUpdatePending,
  error: updateError,
} = updateSiteCurtailmentConfigurationMutation
const {
  mutate: createMutation,
  isPending: isCreatePending,
  error: createError,
} = createSiteCurtailmentConfigurationMutation

const handleOpen = () => {
  formRef.value?.init()
}
const handleClosed = () => {
  formRef.value?.resetFields()
  updateSiteCurtailmentConfigurationMutation.reset()
  createSiteCurtailmentConfigurationMutation.reset()
}
const handleSave = () => {
  formRef.value?.submit()
}

const handleUpdate = (id: number, form: ISiteCurtailmentConfigurationUpsertForm) => {
  updateMutation({ id, form }, { onSuccess: () => (dialogVisible.value = false) })
}

const handleCreate = (form: ISiteCurtailmentConfigurationUpsertForm) => {
  createMutation(form, { onSuccess: () => (dialogVisible.value = false) })
}
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :title="`${props.siteCurtailmentConfiguration ? 'Update' : 'Create'} Site Curtailment Configuration`"
    width="1120px"
    :show-close="false"
    close-on-click-modal
    @open="handleOpen"
    @closed="handleClosed"
  >
    <SiteCurtailmentConfigurationForm
      ref="formRef"
      :config-to-update="props.siteCurtailmentConfiguration"
      @create="handleCreate"
      @update="handleUpdate"
    />
    <RlErrorAlert v-if="updateError" :error="updateError" />
    <RlErrorAlert v-if="createError" :error="createError" />
    <template #footer>
      <el-button @click="dialogVisible = false">Cancel</el-button>
      <el-button type="primary" :loading="isUpdatePending || isCreatePending" @click="handleSave">Save</el-button>
    </template>
  </el-dialog>
</template>
